import { CheckCircleOutlined, CloseCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Circle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { numberFormatter } from '../../utils/numberFormatter';
import { useEffect, useState } from 'react';
import BasicCard from '../basicCard/BasicCard';
import ProgressBar from '../progressBar/ProgressBar';
import { Pagination } from 'antd';
import { TaskPresentationListProps } from '../../types/priceTypes';
import { PriceStatusEnum } from '../../enums/PriceStatusEnum';

const TaskPresentationList: React.FC<TaskPresentationListProps> = ({ presentationsFromTask }) => {
  const [presentationsWithPriceCount, setPresentationsWithPriceCount] = useState(0);
  const { t } = useTranslation(['price', 'task']);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  useEffect(() => {
    const priceCount = presentationsFromTask?.filter(
      (presentation: any) =>
        (presentation &&
          typeof presentation === 'object' &&
          'priceData' in presentation &&
          presentation?.priceAdded === true) ||
        presentation?.isDelegated === true
    ).length;

    setPresentationsWithPriceCount(priceCount);
  }, [presentationsFromTask]);

  useEffect(() => {
    const totalPages = Math.ceil((presentationsFromTask?.length || 0) / pageSize);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [presentationsFromTask, pageSize, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedPresentations =
    presentationsFromTask?.slice(startIndex, startIndex + pageSize) || [];

  return (
    <div className="my-4 ml-0 rounded-lg xl:ml-4">
      <BasicCard>
        <div className="p-4 pb-5">
          <h2 className="mb-4 text-lg font-bold text-black md:text-xl">
            {t('presentationListHeading')}
          </h2>
          <ProgressBar
            percentage={
              Math.min(presentationsWithPriceCount / presentationsFromTask?.length, 1) * 100
            }
          />
          <div className="mb-4 mt-1 text-xs text-gray-500">
            <p>
              {numberFormatter((presentationsWithPriceCount / presentationsFromTask?.length) * 100)}
              % {t('task:completed')}
            </p>
          </div>
          <div className="min-h-[160px] space-y-4">
            {paginatedPresentations.map((presentation: any) => {
              const isDelegated = presentation?.isDelegated === true;
              const hasPrice =
                presentation &&
                typeof presentation === 'object' &&
                'priceData' in presentation &&
                presentation?.priceAdded === true;
              const isNotFound =
                hasPrice &&
                (presentation?.priceData?.status === PriceStatusEnum.PRODUCTNOTFOUND ||
                  presentation?.priceData?.isNotFound);

              return (
                <div key={presentation?.refId || presentation?.id} className="w-full">
                  <h1 className="flex items-center gap-1">
                    {isDelegated ? (
                      <UserOutlined
                        className="font-bold text-blue-500"
                        title={t('markAsDelegated')}
                      />
                    ) : hasPrice ? (
                      isNotFound ? (
                        <CloseCircleOutlined className="text-red-500" />
                      ) : (
                        <CheckCircleOutlined className="text-green-500" />
                      )
                    ) : (
                      <Circle className="mb-1 inline" />
                    )}
                    &nbsp; {typeof presentation === 'object' ? presentation?.name : presentation}
                  </h1>
                </div>
              );
            })}
          </div>
          {presentationsFromTask?.length > 0 && (
            <div className="mt-4 flex w-full justify-center overflow-hidden py-1">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={presentationsFromTask?.length}
                onChange={handlePageChange}
                size="small"
                showSizeChanger={false}
                responsive={true}
              />
            </div>
          )}
        </div>
      </BasicCard>
    </div>
  );
};

export default TaskPresentationList;
